import { useEffect } from 'react';

import { useUser } from '@/hooks';
import { trpc } from '@/services/trpc';
import type { ActiveSubscriptionState } from '@/store/slices/userSlice';

/**
 * This hook should be placed in every base file after auth.
 * It makes sure the user data are not stale and it refreshes
 * them every X seconds.
 */
export function useUserMeData(refreshInterval = 15_000 * 60) {
  const { setUser, currentOrganizationId, setActiveSubscription } = useUser();
  const userQuery = trpc.users.me.useQuery(undefined, {
    staleTime: refreshInterval,
  });
  const activeSubscriptionQuery = trpc.subscriptions.getActive.useQuery(
    { organizationId: currentOrganizationId ?? '' },
    {
      enabled: Boolean(currentOrganizationId),
      staleTime: refreshInterval,
    },
  );

  useEffect(() => {
    if (!userQuery.data) {
      return;
    }

    setUser(userQuery.data);
  }, [setUser, userQuery.data]);

  useEffect(() => {
    if (!currentOrganizationId) {
      setActiveSubscription(null);

      return;
    }

    if (activeSubscriptionQuery.isError) {
      setActiveSubscription(null);

      return;
    }

    if (!activeSubscriptionQuery.data) {
      return;
    }

    setActiveSubscription(
      activeSubscriptionQuery.data as ActiveSubscriptionState,
    );
  }, [
    activeSubscriptionQuery.data,
    activeSubscriptionQuery.isError,
    currentOrganizationId,
    setActiveSubscription,
  ]);
}
